import chatbotClient from './chatbotClient';
import type {
    ChatbotSubdomain, ChatbotSubdomainCreate, ChatbotSubdomainUpdate,
    ChatbotChat, ChatbotChatCreate, ChatbotChatUpdate,
    ChatbotQuiz, ChatbotQuizCreate, ChatbotQuizUpdate,
    ChatbotQuestion, ChatbotQuestionCreate, ChatbotQuestionUpdate,
    ChatbotAnswer, ChatbotAnswerCreate, ChatbotAnswerUpdate,
} from '@/types/chatbot';

// ============ SUBDOMAINS ============
export const listSubdomains = async (parent_domain_id?: number): Promise<ChatbotSubdomain[]> => {
    const r = await chatbotClient.get<ChatbotSubdomain[]>('/subdomains', {
        params: parent_domain_id ? { parent_domain_id } : {},
    });
    return r.data;
};
export const getSubdomain = async (id: number): Promise<ChatbotSubdomain> =>
    (await chatbotClient.get<ChatbotSubdomain>(`/subdomains/${id}`)).data;
export const createSubdomain = async (data: ChatbotSubdomainCreate): Promise<ChatbotSubdomain> =>
    (await chatbotClient.post<ChatbotSubdomain>('/subdomains', data)).data;
export const updateSubdomain = async (id: number, data: ChatbotSubdomainUpdate): Promise<ChatbotSubdomain> =>
    (await chatbotClient.put<ChatbotSubdomain>(`/subdomains/${id}`, data)).data;
export const deleteSubdomain = async (id: number): Promise<{ success: boolean }> =>
    (await chatbotClient.delete<{ success: boolean }>(`/subdomains/${id}`)).data;
export const checkSubdomainSsl = async (id: number): Promise<ChatbotSubdomain> =>
    (await chatbotClient.post<ChatbotSubdomain>(`/subdomains/${id}/check-ssl`)).data;

// ============ CHATS ============
export const listChats = async (subdomain_id: number): Promise<ChatbotChat[]> =>
    (await chatbotClient.get<ChatbotChat[]>('/chats', { params: { subdomain_id } })).data;
export const getChat = async (id: number): Promise<ChatbotChat> =>
    (await chatbotClient.get<ChatbotChat>(`/chats/${id}`)).data;
export const createChat = async (data: ChatbotChatCreate): Promise<ChatbotChat> =>
    (await chatbotClient.post<ChatbotChat>('/chats', data)).data;
export const updateChat = async (id: number, data: ChatbotChatUpdate): Promise<ChatbotChat> =>
    (await chatbotClient.put<ChatbotChat>(`/chats/${id}`, data)).data;
export const deleteChat = async (id: number): Promise<{ success: boolean }> =>
    (await chatbotClient.delete<{ success: boolean }>(`/chats/${id}`)).data;

// ============ CHAT TEMPLATES ============
export interface ChatTemplate {
    key: string;
    name: string;
    description: string;
    questions_count: number;
    quizzes_count: number;
}
export const listChatTemplates = async (): Promise<ChatTemplate[]> =>
    (await chatbotClient.get<ChatTemplate[]>('/chats/templates')).data;
export const createChatFromTemplate = async (
    subdomain_id: number, template_key: string, path?: string,
): Promise<ChatbotChat> =>
    (await chatbotClient.post<ChatbotChat>('/chats/from-template', {
        subdomain_id, template_key, ...(path ? { path } : {}),
    })).data;

// ============ QUIZZES ============
export const listQuizzes = async (chat_id: number): Promise<ChatbotQuiz[]> =>
    (await chatbotClient.get<ChatbotQuiz[]>('/quizzes', { params: { chat_id } })).data;
export const getQuiz = async (id: number): Promise<ChatbotQuiz> =>
    (await chatbotClient.get<ChatbotQuiz>(`/quizzes/${id}`)).data;
export const createQuiz = async (data: ChatbotQuizCreate): Promise<ChatbotQuiz> =>
    (await chatbotClient.post<ChatbotQuiz>('/quizzes', data)).data;
export const updateQuiz = async (id: number, data: ChatbotQuizUpdate): Promise<ChatbotQuiz> =>
    (await chatbotClient.put<ChatbotQuiz>(`/quizzes/${id}`, data)).data;
export const deleteQuiz = async (id: number): Promise<{ success: boolean }> =>
    (await chatbotClient.delete<{ success: boolean }>(`/quizzes/${id}`)).data;
export const listQuizQuestions = async (quiz_id: number): Promise<ChatbotQuestion[]> =>
    (await chatbotClient.get<ChatbotQuestion[]>(`/quizzes/${quiz_id}/questions`)).data;
export const reorderQuizQuestions = async (quiz_id: number, ordered_ids: number[]): Promise<ChatbotQuestion[]> =>
    (await chatbotClient.put<ChatbotQuestion[]>(`/quizzes/${quiz_id}/questions/reorder`, { ordered_ids })).data;

// ============ QUESTIONS ============
export const createQuestion = async (data: ChatbotQuestionCreate): Promise<ChatbotQuestion> =>
    (await chatbotClient.post<ChatbotQuestion>('/questions', data)).data;
export const updateQuestion = async (id: number, data: ChatbotQuestionUpdate): Promise<ChatbotQuestion> =>
    (await chatbotClient.put<ChatbotQuestion>(`/questions/${id}`, data)).data;
export const deleteQuestion = async (id: number): Promise<{ success: boolean }> =>
    (await chatbotClient.delete<{ success: boolean }>(`/questions/${id}`)).data;

// ============ ANSWERS ============
export const createAnswer = async (data: ChatbotAnswerCreate): Promise<ChatbotAnswer> =>
    (await chatbotClient.post<ChatbotAnswer>('/answers', data)).data;
export const updateAnswer = async (id: number, data: ChatbotAnswerUpdate): Promise<ChatbotAnswer> =>
    (await chatbotClient.put<ChatbotAnswer>(`/answers/${id}`, data)).data;
export const deleteAnswer = async (id: number): Promise<{ success: boolean }> =>
    (await chatbotClient.delete<{ success: boolean }>(`/answers/${id}`)).data;

// ============ AD UNITS ============
export interface ResolvedAdUnits {
    topo:         { mobile: string | null; web: string | null };
    interstitial: { mobile: string | null; web: string | null };
    rewarded:     { mobile: string | null; web: string | null };
}
export const resolveAdUnits = async (parent_domain_id: number): Promise<ResolvedAdUnits> =>
    (await chatbotClient.get<ResolvedAdUnits>('/ad-units/resolve', { params: { parent_domain_id } })).data;

export interface CascadePreview {
    prices: number[];
    timeout_ms: number;
    source_cascade_id?: number;
    source_granulation_name?: string;
    source_country?: string;
}
export const previewRewardCascade = async (parent_domain_id: number, country?: string): Promise<CascadePreview> =>
    (await chatbotClient.get<CascadePreview>('/ad-units/cascade-preview', {
        params: { parent_domain_id, ...(country ? { country } : {}) },
    })).data;

// ============ CASCADE GRANULATIONS ============
// Lista vem direto do ms-price-rules (sem proxy via chatbot).
// Endpoint: GET https://price-rule.rocketgroup.io/api/v1/cascade-granulations
// Retorna [{id, name, steps[], created_at, updated_at}, ...]
import axios from 'axios';

export interface CascadeGranulation {
    id: number;
    name: string;
    steps: number[];
}

const PRICE_RULES_API_URL = import.meta.env.VITE_PRICE_RULES_API_URL || 'http://localhost:8082';

const priceRulesAxios = axios.create({
    baseURL: PRICE_RULES_API_URL,
    headers: { 'Content-Type': 'application/json' },
});
priceRulesAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

export const listCascadeGranulations = async (): Promise<CascadeGranulation[]> => {
    const resp = await priceRulesAxios.get<any[]>('/api/v1/cascade-granulations');
    const data = Array.isArray(resp.data) ? resp.data : [];
    return data.map(g => ({
        id: g.id,
        name: g.name || `#${g.id}`,
        steps: Array.isArray(g.steps)
            ? g.steps.map((s: any) => Number(s)).filter((n: number) => !isNaN(n))
            : [],
    }));
};
